;(function (root, factory) {
  if (typeof module === 'object' && typeof module.exports === 'object') {
    module.exports = factory()
  } else {
    var exports = factory()
    if (typeof root === 'object' && root !== null) {
      root.LG = root.LG || {}
      root.LG.cardGallery = exports
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict'

  function normalizeMedia (rawItems) {
    if (!Array.isArray(rawItems)) return []
    var seenIds = new Set()
    var valid = []

    for (var i = 0; i < rawItems.length; i++) {
      var item = rawItems[i]
      if (!item || typeof item !== 'object') continue
      var id = item.id
      if (id === undefined || id === null || id === '') continue
      var strId = String(id).trim()
      if (!strId || seenIds.has(strId)) continue
      var src = typeof item.src === 'string' ? item.src.trim() : ''
      if (!src) continue

      seenIds.add(strId)
      valid.push({
        id: strId,
        src: src,
        srcset: typeof item.srcset === 'string' ? item.srcset.trim() : '',
        sizes: typeof item.sizes === 'string' ? item.sizes.trim() : '',
        width: item.width !== undefined && item.width !== null ? String(item.width) : '',
        height: item.height !== undefined && item.height !== null ? String(item.height) : '',
        alt: typeof item.alt === 'string' ? item.alt : ''
      })
    }
    return valid
  }

  function wrapIndex (index, length) {
    var len = Number(length)
    if (!Number.isFinite(len) || len <= 0) return 0
    var idx = Number(index)
    if (!Number.isFinite(idx)) return 0
    return ((idx % len) + len) % len
  }

  function createRetainedState (maxEntries) {
    var limit = typeof maxEntries === 'number' && maxEntries > 0 ? maxEntries : 100
    var map = new Map()

    return {
      get: function (key) {
        return map.get(key)
      },
      set: function (key, value) {
        if (map.has(key)) {
          map.delete(key)
        } else if (map.size >= limit) {
          var firstKey = map.keys().next().value
          map.delete(firstKey)
        }
        map.set(key, value)
      },
      has: function (key) {
        return map.has(key)
      },
      delete: function (key) {
        return map.delete(key)
      },
      clear: function () {
        map.clear()
      },
      size: function () {
        return map.size
      }
    }
  }

  var defaultRetainedState = createRetainedState(100)

  if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('lg-card-gallery')) {
    var LGCardGallery = function () {
      return Reflect.construct(HTMLElement, [], LGCardGallery)
    }
    Object.setPrototypeOf(LGCardGallery.prototype, HTMLElement.prototype)
    Object.setPrototypeOf(LGCardGallery, HTMLElement)

    LGCardGallery.prototype.connectedCallback = function () {
      if (this.initialized) return
      this.initialized = true

      this.abortController = new AbortController()
      var signal = this.abortController.signal

      this.metadataScript = this.querySelector('.lg-card-gallery__metadata')
      var rawMetadata = []
      if (this.metadataScript && this.metadataScript.textContent) {
        try {
          rawMetadata = JSON.parse(this.metadataScript.textContent)
        } catch (_) {
          rawMetadata = []
        }
      }

      this.images = normalizeMedia(rawMetadata)
      this.visibleImg = this.querySelector('.lg-card-gallery__image')
      this.prevButton = this.querySelector('.lg-card-gallery__control--previous')
      this.nextButton = this.querySelector('.lg-card-gallery__control--next')
      this.statusEl = this.querySelector('.lg-card-gallery__status')
      this.retryButton = this.querySelector('.lg-card-gallery__retry')

      this.productId = this.dataset.productId || ''
      this.contextId = this.dataset.contextId || this.closest('[data-context-id]')?.dataset?.contextId || ''
      this.storageKey = (this.contextId ? this.contextId + ':' : '') + this.productId

      this.requestToken = 0
      this.committedIndex = 0
      this.intendedIndex = 0

      if (this.images.length < 2) {
        if (this.prevButton) {
          this.prevButton.hidden = true
          this.prevButton.disabled = true
        }
        if (this.nextButton) {
          this.nextButton.hidden = true
          this.nextButton.disabled = true
        }
        if (this.statusEl) this.statusEl.hidden = true
        if (this.retryButton) this.retryButton.hidden = true
        return
      }

      // Expose controls for 2+ valid images
      if (this.prevButton) {
        this.prevButton.hidden = false
        this.prevButton.disabled = false
      }
      if (this.nextButton) {
        this.nextButton.hidden = false
        this.nextButton.disabled = false
      }
      if (this.statusEl) this.statusEl.hidden = false
      if (this.retryButton) this.retryButton.hidden = true

      // Restore retained media ID if available
      var initialIndex = 0
      var retainedMediaId = defaultRetainedState.get(this.storageKey)
      if (retainedMediaId) {
        var foundIdx = this.images.findIndex(function (m) { return m.id === retainedMediaId })
        if (foundIdx !== -1) {
          initialIndex = foundIdx
        }
      } else if (this.visibleImg && this.dataset.primaryMediaId) {
        var primaryIdx = this.images.findIndex(function (m) { return m.id === this.dataset.primaryMediaId }.bind(this))
        if (primaryIdx !== -1) {
          initialIndex = primaryIdx
        }
      }

      this.committedIndex = initialIndex
      this.intendedIndex = initialIndex
      this.updateStatus(initialIndex + 1, this.images.length)

      // If initialIndex is not 0 (e.g. restored retained state differs from visible image), commit or transition
      if (initialIndex !== 0) {
        this.navigateTo(initialIndex, false)
      }

      var self = this
      if (this.prevButton) {
        this.prevButton.addEventListener('click', function (event) {
          event.preventDefault()
          event.stopPropagation()
          self.intendedIndex = wrapIndex(self.intendedIndex - 1, self.images.length)
          self.navigateTo(self.intendedIndex, true)
          self.prevButton?.focus()
        }, { signal: signal })
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', function (event) {
          event.preventDefault()
          event.stopPropagation()
          self.intendedIndex = wrapIndex(self.intendedIndex + 1, self.images.length)
          self.navigateTo(self.intendedIndex, true)
          self.nextButton?.focus()
        }, { signal: signal })
      }

      if (this.retryButton) {
        this.retryButton.addEventListener('click', function (event) {
          event.preventDefault()
          event.stopPropagation()
          self.retryButton.hidden = true
          self.navigateTo(self.intendedIndex, true)
        }, { signal: signal })
      }
    }

    LGCardGallery.prototype.disconnectedCallback = function () {
      this.requestToken++
      if (this.abortController) {
        this.abortController.abort()
      }
      this.initialized = false
    }

    LGCardGallery.prototype.updateStatus = function (index, count) {
      if (this.statusEl) {
        this.statusEl.textContent = index + ' / ' + count
      }
    }

    LGCardGallery.prototype.announceFailure = function () {
      if (this.statusEl) {
        this.statusEl.textContent = 'Image failed to load'
      }
    }

    LGCardGallery.prototype.navigateTo = function (targetIndex, isExplicit) {
      var self = this
      var token = ++this.requestToken
      var targetMedia = this.images[targetIndex]
      if (!targetMedia) return

      var detached = new Image()
      if (targetMedia.sizes) detached.sizes = targetMedia.sizes
      if (targetMedia.srcset) detached.srcset = targetMedia.srcset
      detached.src = targetMedia.src
      if (targetMedia.alt) detached.alt = targetMedia.alt

      function commitSuccess () {
        if (self.requestToken !== token) return
        if (!self.visibleImg) return

        if (targetMedia.src) self.visibleImg.src = targetMedia.src
        if (targetMedia.srcset) {
          self.visibleImg.srcset = targetMedia.srcset
        } else {
          self.visibleImg.removeAttribute('srcset')
        }
        if (targetMedia.sizes) {
          self.visibleImg.sizes = targetMedia.sizes
        } else {
          self.visibleImg.removeAttribute('sizes')
        }
        if (targetMedia.alt !== undefined) self.visibleImg.alt = targetMedia.alt
        if (targetMedia.width) self.visibleImg.width = targetMedia.width
        if (targetMedia.height) self.visibleImg.height = targetMedia.height
        self.visibleImg.dataset.mediaId = targetMedia.id

        self.committedIndex = targetIndex
        self.updateStatus(targetIndex + 1, self.images.length)
        if (self.retryButton) self.retryButton.hidden = true

        defaultRetainedState.set(self.storageKey, targetMedia.id)

        if (isExplicit) {
          self.dispatchEvent(new CustomEvent('lg:card-photo:change', {
            bubbles: true,
            composed: true,
            detail: {
              version: 1,
              productId: self.productId,
              mediaId: targetMedia.id,
              index: targetIndex,
              count: self.images.length,
              source: self.dataset.sourceSurface || 'catalog'
            }
          }))
        }
      }

      function handleFailure () {
        if (self.requestToken !== token) return
        self.announceFailure()
        if (self.retryButton) self.retryButton.hidden = false
      }

      if (typeof detached.decode === 'function') {
        detached.decode().then(commitSuccess).catch(function () {
          // If decode fails or image errored, check if complete/naturalWidth or trigger handleFailure
          if (detached.complete && detached.naturalWidth > 0) {
            commitSuccess()
          } else {
            handleFailure()
          }
        })
      } else {
        detached.onload = commitSuccess
        detached.onerror = handleFailure
      }
    }

    window.customElements.define('lg-card-gallery', LGCardGallery)
  }

  return {
    normalizeMedia: normalizeMedia,
    wrapIndex: wrapIndex,
    createRetainedState: createRetainedState
  }
})
